import { cloneDeep, get, isEqual, isNull, isObject, isUndefined, filter } from 'lodash';
import { createSelector } from '@reduxjs/toolkit';

import { DEFAULT_PLAZA_TELEMETRY_TIMEOUT } from './actions';
import { moveThroughHierarchy } from './components/Editor/helper';
import { defaultState } from './reducer';
import { CHARGER_NODE_TYPE, JUNCTION_NODE_TYPE, ROOT_NODE_TYPE } from './components/constants';

const getDataWithoutDynamicSection = (stateData) =>
  cloneDeep(stateData).map((item) => {
    delete item.dynamicSection;
    delete item.schedules;

    return item;
  });

const compareObjectKeys = (objA, objB) => {
  const objAKeys = Object.keys(objA).sort();
  const objBKeys = Object.keys(objB).sort();

  const isEqualData = objAKeys.every((key) => {
    if (objAKeys.length !== objBKeys.length) {
      return false;
    }
    const objDataA = objA[key];
    const objDataB = objB[key];

    if (isUndefined(objDataB)) {
      return false;
    }

    // IMPORTANT
    if (key === 'phases' && ((isNull(objDataA) && !!objDataB) || (!!objDataA && isNull(objDataB)))) {
      return true;
    }

    if (isObject(objDataA) && isObject(objDataB)) {
      return compareObjectKeys(objDataA, objDataB);
    }

    return isEqual(objDataA, objDataB);
  });

  return isEqualData;
};

const compareNodeList = (list = [], initList = []) => {
  if (list.length !== initList.length) {
    return false;
  }

  const results = [];

  for (let i = 0; i < list.length; i++) {
    const objA = list[i];
    const objB = initList[i];

    if (objA && objB) {
      const isEqualData = compareObjectKeys(objA, objB);

      results.push(isEqualData);
    }
  }

  return results.every((item) => item);
};

const searchInTree = (tree = [], id) => {
  const stack = [tree[0]];

  while (stack.length) {
    const node = stack.pop();

    if (node.children.find((item) => item.id === id)) return node;
    node.children && stack.push(...node.children);
  }

  return null;
};

export const getUserSettings = (state) => get(state, 'plazaView.userSettings');

export const isShowPhases = (state) => get(state, 'plazaView.userSettings.showPhases');

export const getViewingNodeData = (state) => get(state, 'plazaView.viewingNode.data');

export const getViewingNodeId = (state) => get(state, 'plazaView.viewingNode.id');

export const plazaFullAvailable = createSelector(
  [(state) => get(state, 'appConfig.config.plazaAvailabilityMode')],
  (value) => value === 'full',
);

export const plazaPartiallyAvailable = createSelector(
  [(state) => get(state, 'appConfig.config.plazaAvailabilityMode')],
  (value) => value === 'partially',
);

export const getNodeList = (state) => get(state, 'plazaTopology.nodeList', []);

export const getJunctionNodes = createSelector([getNodeList], (nodeList) =>
  filter(nodeList, ({ topologyNodeType }) => topologyNodeType === JUNCTION_NODE_TYPE),
);

export const getChargerNodes = createSelector([getNodeList], (nodeList) =>
  filter(nodeList, ({ topologyNodeType }) => topologyNodeType === CHARGER_NODE_TYPE),
);

export const getInitialNodeList = (state) => get(state, 'plazaTopology.initial.nodeList', []);

export const getNode = createSelector([(state) => getNodeList(state), (state, nodeId) => nodeId], (list, nodeId) =>
  nodeId && list ? list.find((node) => node.id === nodeId) : null,
);

export const getExternalSources = createSelector([(state) => get(state, 'plazaTopology.externalSources', [])], (value) => value);

export const getInitialExternalSources = createSelector(
  [(state) => get(state, 'plazaTopology.initial.externalSources', [])],
  (value) => value,
);

export const getExternalSource = createSelector([(state) => getExternalSources(state), (state, sourceId) => sourceId], (list, sourceId) =>
  sourceId && list ? list.find((source) => source.id === sourceId) : null,
);

export const getDynamicData = createSelector([(state) => get(state, 'plazaTopology.dynamicData')], (value) => value);

export const getNodeDynamicData = createSelector([getDynamicData, (state, nodeId) => nodeId], (dynamicData, nodeId) =>
  get(dynamicData, nodeId),
);

export const getHierarchy = createSelector([(state) => get(state, 'plazaTopology.hierarchy', {})], (value) => value);

export const getInitialHierarchy = createSelector([(state) => get(state, 'plazaTopology.initial.hierarchy', {})], (value) => value);

export const getEditableNodeId = createSelector([(state) => get(state, 'plazaTopology.editableNodeId', null)], (value) => value);

export const getEditableNodeParentId = createSelector([getHierarchy, getEditableNodeId], (hierarchy, editableNodeId) => {
  let parentId = null;

  moveThroughHierarchy(hierarchy, ({ parent, child }) => (editableNodeId === child ? (parentId = parent) : null));

  return parentId;
});

export const getEditableNode = createSelector(
  [(state) => getNode(state, getEditableNodeId(state)), (state) => getExternalSource(state, getEditableNodeId(state))],
  (node, externalSource) => node || externalSource,
);

export const getViewingNode = createSelector(
  [(state) => getNode(state, getViewingNodeId(state)), (state) => getExternalSource(state, getViewingNodeId(state))],
  (node, externalSource) => node || externalSource,
);

export const getEditableNodeParent = createSelector([(state) => getNode(state, getEditableNodeParentId(state))], (value) => value);

export const getPlazaData = (state) => get(state, 'plazaView.plaza');

export const getPlazaStatus = createSelector([getPlazaData], (plazaData) => get(plazaData, 'status'));

export const plazaIsActive = createSelector([getPlazaStatus], (plazaStatus) => plazaStatus === 'ACTIVE');

export const getViewingPlazaId = createSelector([getPlazaData], (plazaData) => get(plazaData, 'uuid'));

export const getViewingPlazaGreengrassDevice = createSelector([getPlazaData], (plazaData) => get(plazaData, 'greengrassCoreDeviceId'));

export const getViewingPlazaCustomerId = createSelector([getPlazaData], (plazaData) => get(plazaData, 'customerId'));

export const getViewingPlazaName = createSelector([getPlazaData], (plazaData) => get(plazaData, 'name'));

export const getRequestQueue = (state) => get(state, 'plazaTopology.requestQueue');

export const getZoom = (state) => get(state, 'plazaView.zoom', 0);

export const getMinimapExpanded = (state) => get(state, 'plazaView.minimapExpanded', true);

export const getSchemaParams = (state) => get(state, 'plazaView.schemaParams', defaultState.schemaParams);

export const getAvailablePlazaTemplates = (state) => get(state, 'plazaView.availablePlazaTemplates');

export const checkAllNodesConnected = createSelector(
  [(state) => getDataWithoutDynamicSection(getNodeList(state)), getHierarchy],
  (nodeList, hierarchy) =>
    nodeList
      .filter((node) => node.topologyNodeType !== ROOT_NODE_TYPE)
      .reduce((results, node) => {
        results.push(searchInTree([hierarchy], node.id));

        return results;
      }, [])
      .every(Boolean),
);

export const hasChanges = createSelector(
  [
    (state) => getDataWithoutDynamicSection(getNodeList(state)),
    (state) => getDataWithoutDynamicSection(getInitialNodeList(state)),
    getHierarchy,
    getInitialHierarchy,
    getExternalSources,
    getInitialExternalSources,
  ],
  (nodeList, initNodeList, hierarchy, initHierarchy, externalSources, initExternalSources) =>
    !compareNodeList(nodeList, initNodeList) || !isEqual(hierarchy, initHierarchy) || !isEqual(externalSources, initExternalSources),
);

export const getPlazaEditable = (state) => get(state, 'plazaTopology.editable', false);

export const plazaEditingIsLocked = createSelector([getPlazaEditable], (editable) => !editable);

export const getTemplateType = (state) => get(state, 'plazaView.plaza.templateType');

export const getPlazaSettings = (state) => get(state, 'plazaTopology.settings');

export const getPlazaIdFromState = (state) => get(state, 'plazaTopology.plazaId', null);

export const getPlazaErrors = (state) => get(state, 'plazaTopology.plazaErrors');

export const getNodeErrors = (state) => get(state, 'plazaTopology.nodeErrors');

export const getNodeTopologyErrors = (state) => get(state, 'plazaTopology.nodeTopologyErrors');

export const getTopologyNodes = (state) => get(state, 'plazaView.plaza.topologyNodes', []);

export const plazaLockedByUser = (state) => get(state, 'plazaTopology.lockUserName', '');

export const getShowLoadGraph = (state) => get(state, 'appConfig.config.plaza.rtLoad.show', true);

export const getShowPowerGraph = (state) => get(state, 'appConfig.config.plaza.rtPower.show', true);

export const getLoadChargersLimit = (state) => get(state, 'appConfig.config.plaza.rtLoad.telemetry.maxSelected', 3);

export const getPowerChargersLimit = (state) => get(state, 'appConfig.config.plaza.rtPower.telemetry.maxSelected', 3);

export const getPlazaTelemetryTimeout = (state) =>
  get(state, 'plazaView.plaza.nodeLatestTelemetryTimeout', DEFAULT_PLAZA_TELEMETRY_TIMEOUT);

export const getDefaultDotsLimitLoad = (state) => get(state, 'appConfig.config.plaza.rtLoad.telemetry.defaultDotsLimit', 5000);

export const getDefaultDotsLimitPower = (state) => get(state, 'appConfig.config.plaza.rtPower.telemetry.defaultDotsLimit', 5000);

export const getTelemetryPeriodicityLoad = (state) => get(state, 'appConfig.config.plaza.rtLoad.telemetry.telemetryPeriodicity', 60);

export const getTelemetryPeriodicityPower = (state) => get(state, 'appConfig.config.plaza.rtPower.telemetry.telemetryPeriodicity', 60);

export const getUpdateIntervalLoad = (state) => get(state, 'appConfig.config.plaza.rtLoad.telemetry.minimumUpdatePeriodicity', 60);

export const getUpdateIntervalPower = (state) => get(state, 'appConfig.config.plaza.rtPower.telemetry.minimumUpdatePeriodicity', 60);

export const getCurrentChart = (state) => get(state, 'plazaView.currentChart');

export const getChargersInRows = createSelector(
  [(state) => parseInt(get(state, 'appConfig.config.plaza.chargersInRows', 10), 10)],
  (paramValue) => (isNaN(paramValue) ? 10 : paramValue),
);

export const getMonitorActiveViews = (state) => get(state, 'plazaView.activeViews', []);

export const getTableViewColumnsConfig = (state) => get(state, 'plazaView.columnsConfig', {});
